import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { partnerAPI } from '../services/api';

export default function SettingsPage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Editable fields
  const [displayName, setDisplayName] = useState('');
  const [notifications, setNotifications] = useState({
    access_granted: true,
    access_revoked: true,
    session_summary: false,
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await partnerAPI.getProfile();
      setProfile(data);
      setDisplayName(data.display_name || '');
      if (data.notification_preferences) {
        setNotifications((prev) => ({ ...prev, ...data.notification_preferences }));
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await partnerAPI.updateProfile({
        display_name: displayName,
        notification_preferences: notifications,
      });
      setSuccess('Settings saved successfully');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="pt-page">
      <Box className="pt-head">
        <Box>
          <p className="pt-eyebrow">Partners</p>
          <h1 className="pt-head__title">Settings</h1>
          <p className="pt-head__lede">Your profile and notification preferences.</p>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Profile Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <h2 className="pt-h2">Profile</h2>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fullWidth
            />

            <TextField
              label="Email"
              value={profile?.email || ''}
              fullWidth
              disabled
              helperText="Email cannot be changed"
            />

            <TextField
              label="Company"
              value={profile?.company_name || ''}
              fullWidth
              disabled
              helperText="Contact your administrator to update"
            />

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Status:
              </Typography>
              <Chip
                label={profile?.status || 'unknown'}
                size="small"
                color={profile?.status === 'active' ? 'success' : 'default'}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Notifications Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <h2 className="pt-h2">Email Notifications</h2>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={notifications.access_granted}
                  onChange={(e) =>
                    setNotifications((prev) => ({ ...prev, access_granted: e.target.checked }))
                  }
                />
              }
              label="New tenant access granted"
            />
            <Divider />
            <FormControlLabel
              control={
                <Switch
                  checked={notifications.access_revoked}
                  onChange={(e) =>
                    setNotifications((prev) => ({ ...prev, access_revoked: e.target.checked }))
                  }
                />
              }
              label="Tenant access revoked"
            />
            <Divider />
            <FormControlLabel
              control={
                <Switch
                  checked={notifications.session_summary}
                  onChange={(e) =>
                    setNotifications((prev) => ({ ...prev, session_summary: e.target.checked }))
                  }
                />
              }
              label="Weekly session activity summary"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="large"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
}
